import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb";
import FadeAlert from "../../components/FadeAlert/FadeAlert";
import PolicyForm from "../../components/Forms/PolicyForm.tsx";
import ClientForm from "../../components/Forms/ClientForm.tsx";
import VehicleForm from "../../components/Forms/VehicleForm.tsx";
import PolicyValuesDataGrid from "../../components/DataTables/PolicyValuesDataGrid";
import SearchPolicy from "../../components/SearchPolicy/SearchPolicy.tsx";
import SumsAndDatesForm from "../../components/Forms/SumsAndDatesForm.tsx";
import usePolicyForm from "../../hooks/usePolicyForm";

const AddPolicy: React.FC = () => {
  const navigate = useNavigate();
  const [pageTitle, setPageTitle] = useState("Работа с полици");

  const {
    policyData,
    policyValues,
    insuranceCompanies,
    policyTypes,
    offices,
    agentOptions,
    vehicleTypes,
    vehicleUsages,
    errors,
    warnings,
    payPlaces,
    agents,
    stickers,
    greenCards,
    paymentAccounts,
    setPolicyData,
    addRowsBasedOnInstallments,
    updatePolicyValueRow,
    handleChange,
    saveClientData,
    loadCarDataById,
    loadClientDataByPin,
    handleSearchPolicy,
    savePolicyData,
    saveVehicleData,
    addSingleRow,
    onRowDelete,
    savePolicyInstallments,
    resetForm,
    successMessage,
  } = usePolicyForm();

  useEffect(() => {
    const isEdit = policyData?.policyId && policyData.policyId !== "0";
    setPageTitle(
      isEdit ? "Редакция на съществуваща полица" : "Добавяне на нова полица"
    );
  }, [policyData.policyId]);

  const handleNavigateToAnnexes = useCallback(() => {
    navigate("/policies/change", {
      state: { policyNumber: policyData?.policyNumber },
    });
  }, [navigate, policyData?.policyNumber]);

  const handleDeleteRow = useCallback(
    (row: any) => onRowDelete(row),
    [onRowDelete]
  );

  // Local success banner shown after Save All.
  // We keep it separate from the hook's successMessage so that resetForm()
  // (which wipes hook state) doesn't race with the banner becoming visible.
  const [saveSuccess, setSaveSuccess] = useState("");

  const handleSaveAll = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();

      // 1. Persist the policy header. The returned id is used directly and is
      //    NOT written back to state - that was the source of the re-fetch race.
      const policyId = await savePolicyData();
      if (!policyId) return;

      // 2. Persist installments using the id we just received.
      await savePolicyInstallments(policyId);

      // 3. Show success banner BEFORE resetting so the user sees it.
      setSaveSuccess(`Успешен запис по полица №${policyId}`);

      // 4. Clear the form (RESET_STATE). Because policyId is never written to
      //    state during save, resetting to policyId "0" won't trigger any
      //    effect that would fetch installments.
      resetForm();
    },
    [savePolicyData, savePolicyInstallments, resetForm]
  );

  const getMissingField = useCallback((): string | null => {
    if (policyData.insuranceCompanyId === "") return "Изберете ЗК";
    if (policyData.agentId === "") return "Изберете Агент";
    if (policyData.divisionId === "") return "Изберете Офис";
    if (policyData.policyNumber.length === 0) return "Въведете номер на полица";
    return null;
  }, [
    policyData.insuranceCompanyId,
    policyData.agentId,
    policyData.divisionId,
    policyData.policyNumber,
  ]);

  const missingField = getMissingField();
  const isSaveDisabled = missingField !== null;

  return (
    <>
      <Breadcrumb pageName="Работа с полици" header={pageTitle} />

      {errors.length > 0 && (
        <FadeAlert alert={errors.join(", ")} type="error" onClose={() => {}} />
      )}
      {warnings.length > 0 && (
        <FadeAlert
          alert={warnings.join(", ")}
          type="warning"
          onClose={() => {}}
        />
      )}

      {successMessage || saveSuccess ? (
        <FadeAlert
          alert={successMessage || saveSuccess}
          type="success"
          onClose={() => setSaveSuccess("")}
        />
      ) : null}

      <SearchPolicy
        policyData={policyData}
        onSearch={handleSearchPolicy}
        resetForm={resetForm}
        handleChange={handleChange}
      >
        {policyData?.annexes && (
          <button
            type="button"
            onClick={handleNavigateToAnnexes}
            className="border-stroke hover:border-primary hover:text-primary dark:border-strokedark dark:bg-boxdark flex flex-shrink-0 items-center gap-1.5 rounded border bg-white px-4 py-2 text-sm font-medium text-gray-600 transition dark:text-gray-400"
          >
            <i className="fa-solid fa-file-contract text-primary text-xs" />
            <span className="hidden sm:inline">Добавъци</span>
            <span className="bg-primary flex aspect-square min-w-[20px] items-center justify-center rounded-full p-0.5 text-center text-[11px] font-semibold leading-none text-white">
              {policyData.annexes.length}
            </span>
          </button>
        )}
      </SearchPolicy>

      <PolicyForm
        policyData={policyData}
        handleChange={handleChange}
        insuranceCompanies={insuranceCompanies}
        policyTypes={policyTypes}
        offices={offices}
        agentOptions={agentOptions}
      />
      <ClientForm
        policyData={policyData}
        setPolicyData={setPolicyData}
        handleChange={handleChange}
        handleSearch={loadClientDataByPin}
        handleIdentifierBlur={loadClientDataByPin}
        handleClientSave={saveClientData}
      />

      <div className="mt-2 flex flex-col gap-2">
        <VehicleForm
          policyData={policyData}
          handleChange={handleChange}
          loadCarDataById={loadCarDataById}
          vehicleTypes={vehicleTypes}
          vehicleUsages={vehicleUsages}
          saveVehicleData={saveVehicleData}
        />
        <SumsAndDatesForm
          policyData={policyData}
          handleChange={handleChange}
          setPolicyData={setPolicyData}
          addRowsBasedOnInstallments={addRowsBasedOnInstallments}
        />
      </div>

      <PolicyValuesDataGrid
        policies={policyValues}
        updatePolicyValueRow={updatePolicyValueRow}
        payPlaces={payPlaces}
        agents={agents}
        stickers={stickers}
        greenCards={greenCards}
        paymentAccounts={paymentAccounts}
        addRow={addSingleRow}
        deleteRow={handleDeleteRow}
      />

      <div className="group relative mx-auto mt-4 flex w-1/3 justify-center sm:w-1/6">
        {isSaveDisabled && (
          <div
            role="tooltip"
            className="pointer-events-none absolute -top-14 z-10 flex flex-col items-center opacity-0 transition-all duration-200 group-hover:-translate-y-0.5 group-hover:opacity-100"
          >
            <span
              className="flex items-center gap-2 whitespace-nowrap rounded-lg px-4 py-2.5 text-sm font-medium text-white shadow-lg ring-1 ring-black/10"
              style={{ backgroundColor: "#1f2937" }}
            >
              <i className="fa-solid fa-triangle-exclamation text-amber-400" />
              {missingField}
            </span>
            <span
              className="-mt-1.5 h-3 w-3 rotate-45 shadow-lg"
              style={{ backgroundColor: "#1f2937" }}
            />
          </div>
        )}
        <button
          onClick={handleSaveAll}
          disabled={isSaveDisabled}
          className="bg-primary flex w-full justify-center rounded px-3 py-1.5 text-xs font-medium text-white hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Запис на полица
        </button>
      </div>
    </>
  );
};

export default AddPolicy;
